import React from 'react';
import { ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
interface PaginationProps {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  maxPageButtons?: number;
}
export function Pagination({
  currentPage,
  totalPages,
  onPageChange,
  maxPageButtons = 5
}: PaginationProps) {
  // Don't render pagination if there's only one page
  if (totalPages <= 1) {
    return null;
  }
  // Calculate the range of page buttons to show
  const getPageRange = () => {
    if (totalPages <= maxPageButtons) {
      // If total pages is less than max buttons, show all pages
      return Array.from({
        length: totalPages
      }, (_, i) => i + 1);
    }
    // Calculate start and end of page range
    let start = Math.max(1, currentPage - Math.floor(maxPageButtons / 2));
    let end = start + maxPageButtons - 1;
    // Adjust if end exceeds total pages
    if (end > totalPages) {
      end = totalPages;
      start = Math.max(1, end - maxPageButtons + 1);
    }
    return Array.from({
      length: end - start + 1
    }, (_, i) => start + i);
  };
  const pageRange = getPageRange();
  return <div className="flex items-center justify-center" data-id="element-862">
      <nav className="flex items-center space-x-1" data-id="element-863">
        {/* Previous button */}
        <button onClick={() => onPageChange(currentPage - 1)} disabled={currentPage === 1} className={`px-2 py-2 rounded-md ${currentPage === 1 ? 'text-gray-500 cursor-not-allowed' : 'text-gray-300 hover:bg-gray-700 hover:text-white'}`} aria-label="Previous page" data-id="element-864">
          <ChevronLeftIcon size={16} data-id="element-865" />
        </button>
        {/* First page button with ellipsis if needed */}
        {pageRange[0] > 1 && <>
            <button onClick={() => onPageChange(1)} className={`px-3 py-1 rounded-md ${currentPage === 1 ? 'bg-blue-900/50 text-blue-300 border border-blue-800' : 'text-gray-300 hover:bg-gray-700 hover:text-white'}`} data-id="element-866">
              1
            </button>
            {pageRange[0] > 2 && <span className="px-2 text-gray-500" data-id="element-867">...</span>}
          </>}
        {/* Page buttons */}
        {pageRange.map(page => <button key={page} onClick={() => onPageChange(page)} className={`px-3 py-1 rounded-md ${currentPage === page ? 'bg-blue-900/50 text-blue-300 border border-blue-800' : 'text-gray-300 hover:bg-gray-700 hover:text-white'}`} data-id="element-868">
            {page}
          </button>)}
        {/* Last page button with ellipsis if needed */}
        {pageRange[pageRange.length - 1] < totalPages && <>
            {pageRange[pageRange.length - 1] < totalPages - 1 && <span className="px-2 text-gray-500" data-id="element-869">...</span>}
            <button onClick={() => onPageChange(totalPages)} className={`px-3 py-1 rounded-md ${currentPage === totalPages ? 'bg-blue-900/50 text-blue-300 border border-blue-800' : 'text-gray-300 hover:bg-gray-700 hover:text-white'}`} data-id="element-870">
              {totalPages}
            </button>
          </>}
        {/* Next button */}
        <button onClick={() => onPageChange(currentPage + 1)} disabled={currentPage === totalPages} className={`px-2 py-2 rounded-md ${currentPage === totalPages ? 'text-gray-500 cursor-not-allowed' : 'text-gray-300 hover:bg-gray-700 hover:text-white'}`} aria-label="Next page" data-id="element-871">
          <ChevronRightIcon size={16} data-id="element-872" />
        </button>
      </nav>
    </div>;
}